import { datetimeLocalToIso } from './trackingEventLabels';

/** @typedef {'omit' | 'skip' | 'record'} TrackingEventDecisionMode */

/**
 * @typedef {object} TrackingEventDecision
 * @property {TrackingEventDecisionMode} mode
 * @property {string} [timestampLocal] - datetime-local value when mode is 'record'
 */

export const TRACKING_ENTITY_FIELDS = {
  shipment: ['status', 'location', 'current_location', 'event_type'],
  shipment_unit: ['status', 'event_type', 'location', 'destination_warehouse'],
  shipment_piece: ['status', 'location', 'event_type'],
};

/** Default value for `<input type="datetime-local" />` (local timezone). */
export function defaultDatetimeLocalValue() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function normalizeId(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object' && value.id != null) return String(value.id);
  return String(value);
}

function normalizeFieldValue(field, value) {
  if (field === 'location' || field === 'destination_warehouse' || field === 'current_location') {
    return normalizeId(value);
  }
  if (value == null) return '';
  return String(value).trim();
}

/**
 * Compare original vs current snapshots for tracking-relevant fields.
 * @param {object} params
 * @param {boolean} params.isCreate
 * @param {Record<string, unknown>} [params.original]
 * @param {Record<string, unknown>} params.current
 * @param {'shipment'|'shipment_unit'|'shipment_piece'} params.entityType
 */
export function shouldPromptTrackingEvent({ isCreate, original = {}, current, entityType }) {
  if (isCreate) return true;
  const fields = TRACKING_ENTITY_FIELDS[entityType] || [];
  return fields.some((field) => {
    const a = normalizeFieldValue(field, original[field]);
    const b = normalizeFieldValue(field, current[field]);
    return a !== b;
  });
}

/**
 * Merge write-only tracking control fields into a save payload.
 * @param {Record<string, unknown>} payload
 * @param {TrackingEventDecision|null|undefined} decision
 */
export function applyTrackingEventControl(payload, decision) {
  if (!decision || decision.mode === 'omit') {
    return payload;
  }
  if (decision.mode === 'skip') {
    return { ...payload, create_tracking_event: false };
  }
  if (decision.mode === 'record') {
    const iso = datetimeLocalToIso(decision.timestampLocal);
    // Backend requires tracking_event_timestamp when create_tracking_event is true.
    if (!iso) {
      return payload;
    }
    return {
      ...payload,
      create_tracking_event: true,
      tracking_event_timestamp: iso,
    };
  }
  return payload;
}

/**
 * Build a shipment tracking snapshot from form state.
 */
export function buildShipmentTrackingSnapshot({ status, location, eventType, currentLocationText }) {
  return {
    status: status || '',
    location: location,
    event_type: eventType || '',
    current_location: currentLocationText || '',
  };
}

/**
 * Build a unit tracking snapshot from the unit form object.
 */
export function buildUnitTrackingSnapshot(form) {
  return {
    status: form?.status || '',
    event_type: form?.event_type || '',
    location: form?.location ?? '',
    destination_warehouse: form?.destination_warehouse ?? '',
  };
}

/**
 * Build a piece tracking snapshot from form + location option.
 */
export function buildPieceTrackingSnapshot(form, locationOpt) {
  return {
    status: form?.status || '',
    event_type: form?.event_type || '',
    location: locationOpt?.id ?? form?.location ?? '',
  };
}

/** Default inline form values for tracking control on entity save forms. */
export function defaultTrackingEventFormFields() {
  return {
    tracking_event_mode: 'record',
    tracking_event_timestamp: defaultDatetimeLocalValue(),
  };
}

/**
 * Convert inline form fields to a tracking decision for applyTrackingEventControl.
 * @param {'auto'|'skip'|'record'|string} mode
 * @param {string} [timestampLocal]
 */
export function trackingDecisionFromFormFields(mode, timestampLocal) {
  if (mode === 'skip') return { mode: 'skip' };
  if (mode === 'record') return { mode: 'record', timestampLocal };
  return { mode: 'omit' };
}

/**
 * Client-side validation for inline tracking fields.
 * @returns {string} Error message or empty string.
 */
export function validateTrackingEventFormFields(mode, timestampLocal, t) {
  if (mode === 'record' && (!timestampLocal || !String(timestampLocal).trim())) {
    return t('trackingEventControl.timestampRequired');
  }
  return '';
}
